import type {GameAssets} from './assets';
import type {InputFrame} from './input';

const WORLD_WIDTH = 1280;
const WORLD_HEIGHT = 720;
const GRAVITY = 1650;
const FALL_GRAVITY_MULTIPLIER = 1.08;
const BASE_SCROLL_SPEED = 56;
const PLAYER_RADIUS = 34;
const BASE_JUMP_VELOCITY = 690;
const POWER_JUMP_MULTIPLIER = 1.1;
const BASE_MAX_SPEED = 520;
const GROUND_ACCELERATION = 2550;
const AIR_ACCELERATION = 1650;
const TURN_ACCELERATION_BONUS = 1.35;
const JUMP_BUFFER_MS = 240;
const COYOTE_TIME_MS = 160;
const JUMP_CUT_MULTIPLIER = 0.56;
const RESCUE_SIZE_COST = 0.12;
const REPEAT_RESCUE_SIZE_COST = 0.2;
const PLATFORM_HEIGHT = 72;
const HAZARD_DAMAGE = 0.18;
const HEAL_AMOUNT = 0.18;
const ROUTE_BUFFER_SIZE = 8;
const ROUTE_LOOKAHEAD = 1200;
const PICKUP_DROUGHT_LIMIT = 7;

type Platform = {x: number; y: number; width: number; height: number; hazard: boolean; vx: number};
type Pickup = {x: number; y: number; kind: PowerKind; taken: boolean};
type Splash = {x: number; y: number; age: number};
type PowerKind = 'heal' | 'sticky' | 'slippery' | 'direction-swap' | 'speed' | 'jump' | 'multiball';

type JumpProfile = {
  verticalGap: number;
  horizontalGap: number;
  hopTime: number;
};

type Ball = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  grounded: boolean;
};

type Player = Ball & {
  activePower?: PowerKind;
  pendingPower?: PowerKind;
  powerUntil: number;
};

export type GameStatus = {
  score: number;
  size: number;
  state: 'playing' | 'gameover';
  activePower?: PowerKind;
  pendingPower?: PowerKind;
  ballCount: number;
};

export class TheDripGame {
  private readonly context: CanvasRenderingContext2D;
  private readonly player: Player = this.createPlayer();
  private extraBalls: Ball[] = [];
  private platforms: Platform[] = [];
  private pickups: Pickup[] = [];
  private splashes: Splash[] = [];
  private score = 0;
  private elapsed = 0;
  private randomState = 1;
  private routePredictedSize = 1;
  private platformsSincePickup = 0;
  private platformsSinceHeal = 0;
  private lastPickupElapsed = 0;
  private lastHealElapsed = 0;
  private lastHazardElapsed = -999;
  private lastRescueElapsed = -999;
  private scrollDirection: 1 | -1 = 1;
  private directionSwapUntil = 0;
  private state: 'playing' | 'gameover' = 'playing';
  private jumpBufferUntil = 0;
  private coyoteUntil = 0;
  private jumpCutAllowed = false;
  private lastFrame = performance.now();
  private statusCallback?: (status: GameStatus) => void;

  public constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly assets: GameAssets,
    private readonly readInput: () => InputFrame
  ) {
    const context = canvas.getContext('2d');
    if (!context) {
      throw new Error('Canvas 2D context is unavailable');
    }

    this.context = context;
    this.resize();
    window.addEventListener('resize', () => this.resize());
    this.reset();
  }

  public onStatus(callback: (status: GameStatus) => void): void {
    this.statusCallback = callback;
    this.emitStatus();
  }

  public start(): void {
    this.scheduleNextFrame();
  }

  public reset(): void {
    this.seedRandom(Date.now());
    Object.assign(this.player, this.createPlayer());
    this.player.activePower = undefined;
    this.player.pendingPower = undefined;
    this.extraBalls = [];
    this.platforms = [
      {x: 640, y: 610, width: 880, height: 78, hazard: false, vx: 0},
      {x: 590, y: 480, width: 780, height: 78, hazard: false, vx: 0},
      {x: 700, y: 350, width: 710, height: 78, hazard: false, vx: 0},
      {x: 585, y: 220, width: 650, height: 78, hazard: false, vx: 0},
      {x: 720, y: 90, width: 590, height: 78, hazard: false, vx: 0},
      {x: 640, y: -40, width: 560, height: 78, hazard: false, vx: 0}
    ];
    this.pickups = [
      {x: 790, y: 420, kind: 'heal', taken: false},
      {x: 490, y: 290, kind: 'sticky', taken: false},
      {x: 760, y: 160, kind: 'jump', taken: false},
      {x: 520, y: 30, kind: 'heal', taken: false},
      {x: 830, y: -100, kind: 'speed', taken: false}
    ];
    this.splashes = [];
    this.score = 0;
    this.elapsed = 0;
    this.routePredictedSize = this.player.size;
    this.platformsSincePickup = 0;
    this.platformsSinceHeal = 0;
    this.lastPickupElapsed = 0;
    this.lastHealElapsed = 0;
    this.lastHazardElapsed = -999;
    this.lastRescueElapsed = -999;
    this.scrollDirection = 1;
    this.directionSwapUntil = 0;
    this.state = 'playing';
    this.jumpBufferUntil = 0;
    this.coyoteUntil = 0;
    this.jumpCutAllowed = false;
    this.emitStatus();
  }

  private tick(now: number): void {
    const delta = Math.min(0.033, (now - this.lastFrame) / 1000);
    this.lastFrame = now;
    const input = this.readInput();

    if (input.restartPressed) {
      this.reset();
      this.draw();
      this.scheduleNextFrame();
      return;
    }

    if (this.state === 'gameover') {
      if (input.jumpPressed) {
        this.reset();
      }
      this.draw();
      this.scheduleNextFrame();
      return;
    }

    this.update(delta, input);
    this.draw();
    this.scheduleNextFrame();
  }

  private scheduleNextFrame(): void {
    let handled = false;
    const timeoutId = window.setTimeout(() => {
      if (handled) return;
      handled = true;
      this.tick(performance.now());
    }, 1000 / 60);

    requestAnimationFrame(now => {
      if (handled) return;
      handled = true;
      window.clearTimeout(timeoutId);
      this.tick(now);
    });
  }

  private update(delta: number, input: InputFrame): void {
    this.elapsed += delta;
    this.score += delta * 10;

    const player = this.player;
    if (input.powerPressed) {
      this.activatePendingPower();
    }

    if (performance.now() >= this.directionSwapUntil) {
      this.scrollDirection = 1;
    }

    const agility = clamp(1.55 - player.size * 0.34, 0.82, 1.38);
    const powerSpeed = player.activePower === 'speed' && performance.now() < player.powerUntil ? 1.28 : 1;
    const powerJump = player.activePower === 'jump' && performance.now() < player.powerUntil ? POWER_JUMP_MULTIPLIER : 1;
    const previousY = player.y;
    const now = performance.now();

    if (input.jumpPressed) {
      this.jumpBufferUntil = now + JUMP_BUFFER_MS;
    }

    if (input.jumpReleased && this.jumpCutAllowed && player.vy < -160) {
      player.vy *= JUMP_CUT_MULTIPLIER;
      this.jumpCutAllowed = false;
    }

    const maxSpeed = BASE_MAX_SPEED * powerSpeed * agility;
    const turning = input.moveX !== 0 && Math.sign(input.moveX) !== Math.sign(player.vx) && Math.abs(player.vx) > 40;
    const acceleration = (player.grounded ? GROUND_ACCELERATION : AIR_ACCELERATION) * (turning ? TURN_ACCELERATION_BONUS : 1);
    player.vx += input.moveX * acceleration * powerSpeed * agility * delta;
    player.vx = clamp(player.vx, -maxSpeed, maxSpeed);
    this.applyHorizontalDrag(input.moveX, delta);

    if (this.canJump(now) && this.jumpBufferUntil >= now) {
      this.performJump(powerJump);
    }

    const gravityMultiplier = player.vy > 0 ? FALL_GRAVITY_MULTIPLIER : 1;
    player.vy += GRAVITY * gravityMultiplier * delta;
    player.x += player.vx * delta;
    player.y += player.vy * delta;
    this.resolveWorldEdges();

    const scrollSpeed = (BASE_SCROLL_SPEED + Math.min(84, this.elapsed * 1.9)) * this.scrollDirection;
    for (const platform of this.platforms) {
      platform.y += scrollSpeed * delta;
      platform.x += platform.vx * delta;
      if (platform.x < platform.width / 2 + 18 || platform.x > WORLD_WIDTH - platform.width / 2 - 18) {
        platform.vx *= -1;
        platform.x = clamp(platform.x, platform.width / 2 + 18, WORLD_WIDTH - platform.width / 2 - 18);
      }
    }

    for (const pickup of this.pickups) {
      pickup.y += scrollSpeed * delta;
    }

    this.resolvePlatforms(previousY);
    if (player.grounded) {
      this.coyoteUntil = now + COYOTE_TIME_MS;
    }
    if (this.canJump(now) && this.jumpBufferUntil >= now) {
      this.performJump(powerJump);
    }
    this.resolvePickups();
    this.updateExtraBalls(delta, scrollSpeed);
    this.spawnMore(scrollSpeed);

    this.platforms = this.platforms.filter(platform => platform.y > -ROUTE_LOOKAHEAD && platform.y < WORLD_HEIGHT + ROUTE_LOOKAHEAD);
    this.pickups = this.pickups.filter(pickup => pickup.y > -ROUTE_LOOKAHEAD && pickup.y < WORLD_HEIGHT + ROUTE_LOOKAHEAD && !pickup.taken);

    for (const splash of this.splashes) {
      splash.age += delta;
    }
    this.splashes = this.splashes.filter(splash => splash.age < 0.35);

    if (performance.now() >= player.powerUntil) {
      player.activePower = undefined;
    }

    if (player.y > WORLD_HEIGHT + 220 && this.elapsed < 12) {
      this.recoverPlayer();
    } else if (player.y > WORLD_HEIGHT + 220 && this.canRescueFall()) {
      this.recoverPlayer(this.rescueCost());
    } else if (player.y < -PLAYER_RADIUS * player.size) {
      this.recoverFromTopEdge();
    } else if (player.y > WORLD_HEIGHT + 220 || player.size <= 0.32) {
      this.state = 'gameover';
    }

    this.emitStatus();
  }

  private resolvePlatforms(previousY: number): void {
    const player = this.player;
    player.grounded = false;

    for (const platform of this.platforms) {
      const playerBottom = player.y + PLAYER_RADIUS * player.size;
      const lastBottom = previousY + PLAYER_RADIUS * player.size;
      const platformTop = platform.y - platform.height / 2;
      const withinX = player.x > platform.x - platform.width / 2 - PLAYER_RADIUS * 0.45 && player.x < platform.x + platform.width / 2 + PLAYER_RADIUS * 0.45;
      const crossingTop = lastBottom <= platformTop && playerBottom >= platformTop;

      if (withinX && crossingTop && player.vy >= 0) {
        player.y = platformTop - PLAYER_RADIUS * player.size;
        player.vy = 0;
        player.vx += platform.vx * 0.18;
        player.grounded = true;

        if (platform.hazard) {
          player.size -= HAZARD_DAMAGE;
          this.routePredictedSize = Math.min(this.routePredictedSize, player.size);
          this.lastHazardElapsed = this.elapsed;
          this.splashes.push({x: player.x, y: player.y, age: 0});
        }
      }
    }
  }

  private performJump(powerJump: number): void {
    const topDamping = clamp((this.player.y - 20) / 240, 0.78, 1);
    this.player.vy = -BASE_JUMP_VELOCITY * powerJump * topDamping;
    this.player.grounded = false;
    this.coyoteUntil = 0;
    this.jumpBufferUntil = 0;
    this.jumpCutAllowed = true;
    this.splashes.push({x: this.player.x, y: this.player.y + PLAYER_RADIUS * this.player.size, age: 0});
  }

  private canJump(now: number): boolean {
    return this.player.grounded || now <= this.coyoteUntil;
  }

  private applyHorizontalDrag(moveX: number, delta: number): void {
    const player = this.player;
    const hasInput = Math.abs(moveX) > 0.01;
    const slippery = player.activePower === 'slippery' && performance.now() < player.powerUntil;
    const sticky = player.activePower === 'sticky' && performance.now() < player.powerUntil;
    const drag = hasInput
      ? (player.grounded ? 1.1 : 0.7)
      : player.grounded
        ? (sticky ? 17 : slippery ? 2.1 : 11)
        : (slippery ? 0.18 : 0.9);
    player.vx *= Math.exp(-drag * delta);
    if (!hasInput && Math.abs(player.vx) < 4) {
      player.vx = 0;
    }
  }

  private recoverPlayer(sizeCost = 0): void {
    const platformHeight = 72;
    const playerBottomOffset = PLAYER_RADIUS * this.player.size;
    const platformY = WORLD_HEIGHT - 82;
    if (sizeCost > 0) {
      this.player.size = Math.max(0.34, this.player.size - sizeCost);
      this.routePredictedSize = Math.min(this.routePredictedSize, this.player.size);
      this.lastRescueElapsed = this.elapsed;
      this.platformsSinceHeal = Math.max(this.platformsSinceHeal, 5);
    }
    this.platforms.push({
      x: this.player.x,
      y: platformY,
      width: sizeCost > 0 ? 620 : 700,
      height: platformHeight,
      hazard: false,
      vx: 0
    });
    this.player.y = platformY - platformHeight / 2 - playerBottomOffset;
    this.player.vy = 0;
    this.player.grounded = true;
    this.coyoteUntil = performance.now() + COYOTE_TIME_MS;
    this.jumpCutAllowed = false;
    this.splashes.push({x: this.player.x, y: this.player.y + playerBottomOffset, age: 0});
  }

  private canRescueFall(): boolean {
    return this.player.size > 0.36;
  }

  private rescueCost(): number {
    const targetCost = this.elapsed - this.lastRescueElapsed < 14 ? REPEAT_RESCUE_SIZE_COST : RESCUE_SIZE_COST;
    return Math.max(0.02, Math.min(targetCost, this.player.size - 0.34));
  }

  private recoverFromTopEdge(): void {
    const radius = PLAYER_RADIUS * this.player.size;
    this.player.y = -radius;
    if (this.player.vy < 0) {
      this.player.vy *= -0.18;
    }
    this.jumpCutAllowed = false;
  }

  private resolveWorldEdges(): void {
    const radius = PLAYER_RADIUS * this.player.size;
    const left = radius;
    const right = WORLD_WIDTH - radius;
    if (this.player.x < left) {
      this.player.x = left;
      this.player.vx = Math.max(0, this.player.vx) * 0.35;
    } else if (this.player.x > right) {
      this.player.x = right;
      this.player.vx = Math.min(0, this.player.vx) * 0.35;
    }
  }

  private updateExtraBalls(delta: number, scrollSpeed: number): void {
    const aliveBalls: Ball[] = [];

    for (const ball of this.extraBalls) {
      const previousY = ball.y;
      ball.vy += GRAVITY * delta;
      ball.x += ball.vx * delta;
      ball.y += ball.vy * delta + scrollSpeed * 0.18 * delta;
      ball.x = clamp(ball.x, PLAYER_RADIUS, WORLD_WIDTH - PLAYER_RADIUS);
      ball.grounded = false;

      for (const platform of this.platforms) {
        const ballBottom = ball.y + PLAYER_RADIUS * ball.size;
        const lastBottom = previousY + PLAYER_RADIUS * ball.size;
        const platformTop = platform.y - platform.height / 2;
        const withinX = ball.x > platform.x - platform.width / 2 - PLAYER_RADIUS * 0.35 && ball.x < platform.x + platform.width / 2 + PLAYER_RADIUS * 0.35;
        const crossingTop = lastBottom <= platformTop && ballBottom >= platformTop;

        if (withinX && crossingTop && ball.vy >= 0) {
          ball.y = platformTop - PLAYER_RADIUS * ball.size;
          ball.vy = -520;
          ball.vx += platform.vx * 0.16;
          ball.grounded = true;
          if (platform.hazard) {
            ball.size -= 0.08;
          }
        }
      }

      const distanceToPlayer = Math.hypot(ball.x - this.player.x, ball.y - this.player.y);
      const touchDistance = PLAYER_RADIUS * (ball.size + this.player.size);
      if (distanceToPlayer > 0 && distanceToPlayer < touchDistance) {
        const pushX = (ball.x - this.player.x) / distanceToPlayer;
        const pushY = (ball.y - this.player.y) / distanceToPlayer;
        ball.vx += pushX * 160 * delta;
        ball.vy += pushY * 120 * delta;
        this.player.vx -= pushX * 110 * delta;
      }

      if (ball.y > -160 && ball.y < WORLD_HEIGHT + 180 && ball.size > 0.22) {
        aliveBalls.push(ball);
      }
    }

    this.extraBalls = aliveBalls;
  }

  private resolvePickups(): void {
    for (const pickup of this.pickups) {
      const distance = Math.hypot(this.player.x - pickup.x, this.player.y - pickup.y);
      if (distance < 58 * this.player.size) {
        pickup.taken = true;
        this.applyPower(pickup.kind);
        this.splashes.push({x: pickup.x, y: pickup.y, age: 0});
      }
    }
  }

  private applyPower(kind: PowerKind): void {
    if (kind === 'heal') {
      this.player.size = Math.min(1.35, this.player.size + HEAL_AMOUNT);
      this.routePredictedSize = Math.max(this.routePredictedSize, this.player.size);
      this.platformsSinceHeal = 0;
      this.lastHealElapsed = this.elapsed;
      this.platformsSincePickup = 0;
      this.lastPickupElapsed = this.elapsed;
      return;
    }

    this.platformsSincePickup = 0;
    this.lastPickupElapsed = this.elapsed;
    this.player.pendingPower = kind;
  }

  private activatePendingPower(): void {
    const kind = this.player.pendingPower;
    if (!kind) {
      return;
    }

    this.player.pendingPower = undefined;

    if (kind === 'multiball') {
      this.spawnExtraBalls();
      return;
    }

    if (kind === 'direction-swap') {
      this.scrollDirection = -1;
      this.directionSwapUntil = performance.now() + 4200;
      this.player.activePower = kind;
      this.player.powerUntil = this.directionSwapUntil;
      return;
    }

    this.player.activePower = kind;
    this.player.powerUntil = performance.now() + 5200;
  }

  private spawnExtraBalls(): void {
    const baseSize = Math.max(0.52, this.player.size * 0.72);
    this.extraBalls = [
      {x: this.player.x - 38, y: this.player.y - 18, vx: this.player.vx - 260, vy: -540, size: baseSize, grounded: false},
      {x: this.player.x + 38, y: this.player.y - 18, vx: this.player.vx + 260, vy: -540, size: baseSize, grounded: false}
    ];
    this.score += 35;
    this.splashes.push({x: this.player.x, y: this.player.y, age: 0});
  }

  private spawnMore(scrollSpeed: number): void {
    void scrollSpeed;
    const futureCount = this.countFuturePlatforms();
    for (let index = futureCount; index < ROUTE_BUFFER_SIZE; index++) {
      this.spawnPlannedPlatform();
    }
  }

  private spawnPlannedPlatform(): void {
    const anchor = this.findRouteAnchor();
    const difficulty = this.difficulty();
    let candidate: Platform | undefined;

    for (let attempt = 0; attempt < 24; attempt++) {
      const next = this.createPlatformCandidate(anchor, difficulty, attempt);
      if (this.isReachableHop(anchor, next, difficulty) && !this.hasPlatformOverlap(next)) {
        candidate = next;
        break;
      }
    }

    if (!candidate) {
      candidate = this.createFallbackPlatform(anchor, difficulty);
    }

    this.platforms.push(candidate);
    this.platformsSincePickup++;
    this.platformsSinceHeal++;
    if (candidate.hazard) {
      this.routePredictedSize = Math.max(0, this.routePredictedSize - HAZARD_DAMAGE);
    }

    const pickupKind = this.selectPlannedPickup(candidate);
    if (pickupKind) {
      this.pickups.push(this.createRoutePickup(anchor, candidate, pickupKind));
      this.platformsSincePickup = 0;
      this.lastPickupElapsed = this.elapsed;
      if (pickupKind === 'heal') {
        this.platformsSinceHeal = 0;
        this.lastHealElapsed = this.elapsed;
        this.routePredictedSize = Math.min(1.35, this.routePredictedSize + HEAL_AMOUNT);
      }
    }
  }

  private createPlatformCandidate(anchor: Platform, difficulty: number, attempt: number): Platform {
    const profile = this.calculateJumpProfile(anchor.y, difficulty);
    const opening = this.elapsed < 120;
    const gapMin = opening ? 114 : 108;
    const gapMax = Math.max(gapMin + 8, profile.verticalGap);
    const verticalGap = lerp(gapMin, gapMax, this.random() * (opening ? 0.58 : 0.82));
    const y = anchor.y + (this.scrollDirection > 0 ? -verticalGap : verticalGap);
    const width = lerp(opening ? 820 : 720, opening ? 580 : 440, difficulty);
    const landingAllowance = Math.max(0, (anchor.width + width) / 2 - PLAYER_RADIUS * this.player.size * 1.2);
    const horizontalBudget = clamp(profile.horizontalGap + landingAllowance * 0.38, 210, opening ? 450 : 490);
    const direction = attempt % 3 === 0 ? 0 : (this.random() < 0.5 ? -1 : 1);
    const xOffset = direction * lerp(34, horizontalBudget, this.random());
    const x = clamp(anchor.x + xOffset, 60 + width / 2, WORLD_WIDTH - 60 - width / 2);
    const moverAllowed = this.elapsed > 60 && this.player.size > 0.72 && this.elapsed - this.lastHazardElapsed > 12;
    const vx = moverAllowed && this.random() < 0.12 + difficulty * 0.08
      ? (this.random() < 0.5 ? -1 : 1) * lerp(24, 72, difficulty)
      : 0;
    const hazard = this.shouldMakeHazard(difficulty);
    return {x, y, width, height: PLATFORM_HEIGHT, hazard, vx};
  }

  private createFallbackPlatform(anchor: Platform, difficulty: number): Platform {
    const profile = this.calculateJumpProfile(anchor.y, difficulty);
    const verticalGap = clamp(profile.verticalGap * 0.86, 108, 132);
    const width = this.elapsed < 120 ? 720 : 600;
    const x = clamp(anchor.x + (anchor.x < WORLD_WIDTH / 2 ? 70 : -70), 60 + width / 2, WORLD_WIDTH - 60 - width / 2);
    const y = anchor.y + (this.scrollDirection > 0 ? -verticalGap : verticalGap);
    return {x, y, width, height: PLATFORM_HEIGHT, hazard: false, vx: 0};
  }

  private calculateJumpProfile(anchorY: number, difficulty: number): JumpProfile {
    const playerSize = Math.max(this.player.size, 0.82);
    const agility = clamp(1.55 - playerSize * 0.34, 0.82, 1.38);
    const topDamping = clamp((anchorY - 40) / 260, 0.62, 1);
    const jumpVelocity = BASE_JUMP_VELOCITY * topDamping;
    const rawJumpHeight = (jumpVelocity * jumpVelocity) / (2 * GRAVITY * FALL_GRAVITY_MULTIPLIER);
    const hopTime = clamp((jumpVelocity / GRAVITY) * 1.48, 0.5, 0.84);
    const scrollAssist = (BASE_SCROLL_SPEED + Math.min(84, this.elapsed * 1.9)) * hopTime * 0.24;
    const verticalGap = clamp(rawJumpHeight * lerp(0.82, 0.92, difficulty) + scrollAssist, this.elapsed < 120 ? 118 : 112, this.elapsed < 120 ? 150 : 162);
    const horizontalGap = clamp(BASE_MAX_SPEED * agility * hopTime * lerp(0.42, 0.54, difficulty), 190, this.elapsed < 120 ? 305 : 350);
    return {verticalGap, horizontalGap, hopTime};
  }

  private isReachableHop(from: Platform, to: Platform, difficulty: number): boolean {
    const profile = this.calculateJumpProfile(from.y, difficulty);
    const verticalGap = Math.abs(to.y - from.y);
    const movingDrift = Math.abs(to.vx) * profile.hopTime;
    const landingAllowance = Math.max(0, (from.width + to.width) / 2 - PLAYER_RADIUS * this.player.size * 1.2);
    const horizontalGap = Math.max(0, Math.abs(to.x - from.x) + movingDrift - landingAllowance * 0.45);
    return verticalGap <= profile.verticalGap && horizontalGap <= profile.horizontalGap;
  }

  private hasPlatformOverlap(candidate: Platform): boolean {
    for (const platform of this.platforms) {
      const yDistance = Math.abs(candidate.y - platform.y);
      if (yDistance < 92) return true;
      const overlap = Math.min(candidate.x + candidate.width / 2, platform.x + platform.width / 2) - Math.max(candidate.x - candidate.width / 2, platform.x - platform.width / 2);
      if (yDistance < 128 && overlap > Math.min(candidate.width, platform.width) * 0.78) return true;
    }
    return false;
  }

  private shouldMakeHazard(difficulty: number): boolean {
    if (this.elapsed < 75) return false;
    if (this.player.size < 0.9 || this.routePredictedSize < this.sizeSafetyFloor() + HAZARD_DAMAGE + 0.08) return false;
    if (this.platformsSinceHeal > 4 || this.elapsed - this.lastHazardElapsed < 16) return false;
    return this.random() < 0.025 + difficulty * 0.045;
  }

  private selectPlannedPickup(platform: Platform): PowerKind | undefined {
    const forceAny = this.platformsSincePickup >= (this.elapsed < 120 ? 3 : PICKUP_DROUGHT_LIMIT);
    const forceHeal = this.needsHealSoon(platform.hazard);
    const spawnChance = this.elapsed < 120 ? 0.56 : 0.4;
    if (!forceAny && !forceHeal && this.random() > spawnChance) return undefined;
    if (forceHeal) return 'heal';
    return this.selectBudgetedPowerup();
  }

  private needsHealSoon(candidateIsHazard: boolean): boolean {
    const projectedSize = this.routePredictedSize - (candidateIsHazard ? HAZARD_DAMAGE : 0);
    if (projectedSize < Math.max(this.sizeSafetyFloor() + 0.14, 0.92)) return true;
    if (this.platformsSinceHeal >= (this.elapsed < 150 ? 5 : 7)) return true;
    return this.elapsed - this.lastHealElapsed > (this.elapsed < 150 ? 28 : 38) && this.player.size < 1.2;
  }

  private selectBudgetedPowerup(): PowerKind {
    if (this.player.size < 1 || this.routePredictedSize < 1) return 'heal';
    const early = this.elapsed < 120;
    const roll = this.random();
    if (early) {
      if (roll < 0.28) return 'jump';
      if (roll < 0.5) return 'speed';
      if (roll < 0.72) return 'sticky';
      return 'heal';
    }
    if (roll < 0.24) return 'jump';
    if (roll < 0.46) return 'speed';
    if (roll < 0.6) return 'sticky';
    if (roll < 0.78) return 'heal';
    if (roll < 0.88 && this.player.size > 1) return 'multiball';
    if (roll < 0.94 && this.player.size > 1.12) return 'slippery';
    return 'heal';
  }

  private createRoutePickup(anchor: Platform, platform: Platform, kind: PowerKind): Pickup {
    const targetX = kind === 'heal' ? platform.x : lerp(anchor.x, platform.x, 0.62);
    const safeOffset = (this.random() - 0.5) * Math.min(kind === 'heal' ? 110 : 190, platform.width * 0.3);
    return {
      x: clamp(targetX + safeOffset, platform.x - platform.width / 2 + 70, platform.x + platform.width / 2 - 70),
      y: platform.y + (this.scrollDirection > 0 ? -78 : 78),
      kind,
      taken: false
    };
  }

  private countFuturePlatforms(): number {
    if (this.scrollDirection > 0) {
      return this.platforms.filter(platform => platform.y < this.player.y && platform.y > -ROUTE_LOOKAHEAD).length;
    }
    return this.platforms.filter(platform => platform.y > this.player.y && platform.y < WORLD_HEIGHT + ROUTE_LOOKAHEAD).length;
  }

  private findRouteAnchor(): Platform {
    return this.platforms.reduce((best, platform) => {
      if (this.scrollDirection > 0) return platform.y < best.y ? platform : best;
      return platform.y > best.y ? platform : best;
    }, this.platforms[0]);
  }

  private sizeSafetyFloor(): number {
    if (this.elapsed < 120) return 0.78;
    if (this.elapsed < 240) return 0.64;
    return 0.5;
  }

  private difficulty(): number {
    return Math.min(1, this.elapsed / 180);
  }

  private seedRandom(seed: number): void {
    this.randomState = seed >>> 0 || 1;
  }

  private random(): number {
    this.randomState = (this.randomState + 0x6d2b79f5) | 0;
    let value = this.randomState;
    value = Math.imul(value ^ (value >>> 15), value | 1);
    value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
    return ((value ^ (value >>> 14)) >>> 0) / 4294967296;
  }

  private draw(): void {
    const context = this.context;
    context.clearRect(0, 0, WORLD_WIDTH, WORLD_HEIGHT);
    this.drawBackground(context);

    for (const platform of this.platforms) {
      const image = platform.hazard ? this.assets['hazard-thorn-puddle'] : this.assets['platform-wet-tile'];
      context.drawImage(image, platform.x - platform.width / 2, platform.y - platform.height / 2, platform.width, platform.height);
    }

    for (const pickup of this.pickups) {
      const image = this.assets[powerAsset(pickup.kind)];
      context.drawImage(image, pickup.x - 32, pickup.y - 32, 64, 64);
    }

    for (const splash of this.splashes) {
      const alpha = 1 - splash.age / 0.35;
      context.globalAlpha = alpha;
      context.drawImage(this.assets['splash-burst'], splash.x - 46, splash.y - 46, 92, 92);
      context.globalAlpha = 1;
    }

    const playerSize = PLAYER_RADIUS * 2 * this.player.size;
    context.drawImage(this.assets['player-droplet'], this.player.x - playerSize / 2, this.player.y - playerSize / 2, playerSize, playerSize);

    for (const ball of this.extraBalls) {
      const ballSize = PLAYER_RADIUS * 2 * ball.size;
      context.globalAlpha = 0.82;
      context.drawImage(this.assets['player-droplet'], ball.x - ballSize / 2, ball.y - ballSize / 2, ballSize, ballSize);
      context.globalAlpha = 1;
    }

    context.drawImage(this.assets['logo-the-drip'], 24, 18, 188, 106);
    context.fillStyle = '#f7f9ff';
    context.font = '700 28px Arial, Helvetica, sans-serif';
    context.fillText(String(Math.floor(this.score)).padStart(6, '0'), WORLD_WIDTH - 170, 48);
    context.font = '600 18px Arial, Helvetica, sans-serif';
    context.fillText(`SIZE ${this.player.size.toFixed(2)}`, WORLD_WIDTH - 170, 78);

    if (this.player.activePower) {
      context.fillStyle = '#ffe36e';
      context.fillText(this.player.activePower.toUpperCase(), WORLD_WIDTH / 2 - 70, 48);
    }

    if (this.player.pendingPower) {
      context.fillStyle = '#a7fff1';
      context.fillText(`READY ${this.player.pendingPower.toUpperCase()}`, WORLD_WIDTH / 2 - 96, 78);
    }

    if (this.state === 'gameover') {
      context.fillStyle = 'rgba(7, 10, 18, 0.72)';
      context.fillRect(0, 0, WORLD_WIDTH, WORLD_HEIGHT);
      context.fillStyle = '#ffffff';
      context.font = '800 58px Arial, Helvetica, sans-serif';
      context.textAlign = 'center';
      context.fillText('Game Over', WORLD_WIDTH / 2, WORLD_HEIGHT / 2 - 42);
      context.font = '600 24px Arial, Helvetica, sans-serif';
      context.fillText('Press Enter or Space to restart', WORLD_WIDTH / 2, WORLD_HEIGHT / 2 + 10);
      context.drawImage(this.assets['restart-button'], WORLD_WIDTH / 2 - 44, WORLD_HEIGHT / 2 + 42, 88, 88);
      context.textAlign = 'left';
    }
  }

  private resize(): void {
    const ratio = window.devicePixelRatio || 1;
    this.canvas.width = WORLD_WIDTH * ratio;
    this.canvas.height = WORLD_HEIGHT * ratio;
    this.canvas.style.aspectRatio = `${WORLD_WIDTH} / ${WORLD_HEIGHT}`;
    this.context.setTransform(ratio, 0, 0, ratio, 0, 0);
  }

  private drawBackground(context: CanvasRenderingContext2D): void {
    const gradient = context.createLinearGradient(0, 0, 0, WORLD_HEIGHT);
    gradient.addColorStop(0, '#071426');
    gradient.addColorStop(0.52, '#0f8090');
    gradient.addColorStop(1, '#07333f');
    context.fillStyle = gradient;
    context.fillRect(0, 0, WORLD_WIDTH, WORLD_HEIGHT);

    context.fillStyle = 'rgba(126, 239, 226, 0.16)';
    for (let index = 0; index < 7; index++) {
      const x = -120 + index * 250;
      const y = 170 + Math.sin(index * 1.7) * 40;
      context.beginPath();
      context.ellipse(x + 140, y + 80, 190, 110, 0, 0, Math.PI * 2);
      context.fill();
    }

    context.strokeStyle = 'rgba(188, 244, 255, 0.38)';
    context.lineWidth = 2;
    const rainOffset = (this.elapsed * 180) % 90;
    for (let x = -80; x < WORLD_WIDTH + 80; x += 42) {
      const y = ((x * 3 + rainOffset * 4) % (WORLD_HEIGHT + 110)) - 80;
      context.beginPath();
      context.moveTo(x, y);
      context.lineTo(x - 20, y + 58);
      context.stroke();
    }

    context.fillStyle = 'rgba(2, 10, 18, 0.38)';
    context.fillRect(0, WORLD_HEIGHT - 95, WORLD_WIDTH, 95);
  }

  private createPlayer(): Player {
    return {
      x: WORLD_WIDTH / 2,
      y: 520,
      vx: 0,
      vy: 0,
      size: 1,
      grounded: false,
      powerUntil: 0
    };
  }

  private emitStatus(): void {
    this.statusCallback?.({
      score: this.score,
      size: this.player.size,
      state: this.state,
      activePower: this.player.activePower,
      pendingPower: this.player.pendingPower,
      ballCount: 1 + this.extraBalls.length
    });
  }
}

function powerAsset(kind: PowerKind): keyof GameAssets {
  switch (kind) {
    case 'heal':
      return 'power-heal';
    case 'sticky':
      return 'power-sticky';
    case 'slippery':
      return 'power-slippery';
    case 'direction-swap':
      return 'power-direction-swap';
    case 'speed':
      return 'power-speed';
    case 'jump':
      return 'power-jump';
    case 'multiball':
      return 'power-multiball';
  }
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function lerp(start: number, end: number, value: number): number {
  return start + (end - start) * value;
}
